import React, { useEffect, useState } from 'react';
import { ToastMessage, ToastEvent } from '../../Models/Toast/ToastModels';
import ToastService from '../../AppSettings/services/ToastService';
import './ToastContainer.css';

/**
 * ToastContainer Component
 *
 * A global toast notification container that displays all toasts
 * managed by the ToastService singleton. This component should be
 * included once in your app (typically in App.tsx) to enable
 * global toast notifications.
 */
const ToastContainer: React.FC = () => {
  const [visibleToasts, setVisibleToasts] = useState<ToastMessage[]>([]);

  useEffect(() => {
    // Listen to toast service events
    const unsubscribe = ToastService.addEventListener((event: ToastEvent) => {
      switch (event.type) {
        case 'add':
          // Update visible toasts when a new toast is added
          setVisibleToasts(ToastService.getVisible());
          break;
        case 'remove':
          // Update visible toasts when a toast is removed
          setVisibleToasts(ToastService.getVisible());
          break;
        case 'clear':
          // Clear all toasts
          setVisibleToasts([]);
          break;
        case 'update':
          // Update the specific toast
          setVisibleToasts(ToastService.getVisible());
          break;
      }
    });

    // Initialize with current visible toasts
    setVisibleToasts(ToastService.getVisible());

    // Cleanup listener on unmount
    return unsubscribe;
  }, []);

  const handleDismiss = (toastId: string) => {
    ToastService.dismiss(toastId);
  };

  const handleAction = (toast: ToastMessage) => {
    if (toast.onAction) {
      toast.onAction();
    }
    // Auto-dismiss after action if dismissible
    if (toast.dismissible) {
      ToastService.dismiss(toast.id);
    }
  };

  if (visibleToasts.length === 0) {
    return null;
  }

  return (
    <div className="toast-container">
      {visibleToasts.map((toast) => (
        <div
          key={toast.id}
          className={`toast toast--${toast.type} ${
            toast.dismissible ? 'toast--dismissible' : ''
          }`}
          role="alert"
          aria-live="assertive"
        >
          <div className="toast__content">
            <div className="toast__message">{toast.message}</div>
            {toast.description && (
              <div className="toast__description">{toast.description}</div>
            )}
            {toast.actionText && (
              <button
                className="toast__action"
                onClick={() => handleAction(toast)}
                type="button"
              >
                {toast.actionText}
              </button>
            )}
          </div>
          {toast.dismissible && (
            <button
              className="toast__dismiss"
              onClick={() => handleDismiss(toast.id)}
              type="button"
              aria-label="Dismiss notification"
            >
              ×
            </button>
          )}
        </div>
      ))}
    </div>
  );
};

export default ToastContainer;
